import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Outlet, useNavigate, useParams } from 'react-router-dom';

import {
  PermissionKeys,
  SpaceDiscriminator,
  useGetSpacesByIdHierarchyQuery,
  usePutSpacesByIdChildrenMutation,
} from '@client/api';
import { Button, PageBreadcrumbs, PageNavigation, Skeleton, useRouteDocumentTitle } from '@client/toolkit';
import { DropOptions, NodeModel, Tree } from '@minoru/react-dnd-treeview';

import { SpaceDragPreview } from './components/space-drag-preview.component';
import { SpaceNode } from './components/space-node.component';
import { SpaceTreeDisplay } from './components/space-tree-display.component';
import { SpaceTreePlaceholder } from './components/space-tree-placeholder.component';
import { SpaceNodeData, useSpaceTreeData } from './hooks/use-space-tree-data';
import { useSpaceTreeTags } from './hooks/use-space-tree-tags';

import { BackButton } from '../../../components/back-button/back-button.component';
import { PortfolioPaths } from '../portfolio.router';
import { PageWithSidebarLayout, PageContentAnimation } from '../../../components/layout';
import { HasSpacePermission } from '../../../components/permission/has-space-permission';
import { NoPackageAccessPage } from '../../../components/no-access-modal';

// just for loading
const skeletonTreeData = Array.from(Array(10).keys()).map<NodeModel<SpaceNodeData>>((x) => {
  let parent: string | number = 0;
  if (x === 0) {
    parent = 'root';
  }
  if (x > 6 && x <= 8) {
    parent = 6;
  }
  return { id: x, parent: parent, text: '' };
});

export function SpaceHierarchyRoute() {
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tCommon } = useTranslation();

  useRouteDocumentTitle(t('documentTitle'), PortfolioPaths.Building);

  const { buildingId, spaceId } = useParams<{ buildingId: string; spaceId: string }>();

  const navigate = useNavigate();

  const {
    data: spaceHierarchy,
    isFetching,
    isLoading,
  } = useGetSpacesByIdHierarchyQuery({
    id: buildingId ?? '',
  });

  const { treeData, updateTree, filterTreeByParentId, isNodeAncestorOf, filterTreeByAncestorOfChildId } =
    useSpaceTreeData(spaceHierarchy);
  const [openIds, setOpenIds] = useState<NodeModel['id'][]>(buildingId ? [buildingId] : []);

  const handleSelectBuildingsLinks = useCallback(() => navigate(generatePath(PortfolioPaths.BuildingList)), [navigate]);

  const [mutation] = usePutSpacesByIdChildrenMutation();

  const handleDrop = async (
    updatedTree: NodeModel<SpaceNodeData>[],
    { dropTargetId, dragSourceId }: DropOptions<SpaceNodeData>,
  ) => {
    const newTree = updateTree(updatedTree);
    const parentId = `${dropTargetId}`;
    const children = filterTreeByParentId(newTree, parentId).map((x) => `${x.id}`);

    try {
      await mutation({
        id: parentId,
        body: {
          children,
        },
      }).unwrap();
    } catch (e) {
      return;
    }
  };

  // this will make sure that the current active space and all of his ancestor nodes will be always uncollapsed in the space hierarchy tree list
  useEffect(() => {
    if (spaceId == null) return;

    const ancestorTree = filterTreeByAncestorOfChildId(treeData, spaceId);

    if (!Array.isArray(ancestorTree) || ancestorTree.length <= 0) return;

    const ancestorIds = ancestorTree.map((x) => x.id);
    const newOpenIds = [...new Set([...openIds, ...ancestorIds])];
    setOpenIds(newOpenIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId, treeData]);

  const { hasTags, tagCount } = useSpaceTreeTags();
  const [displayOpen, setDisplayOpen] = useState(hasTags);

  return (
    <HasSpacePermission id={buildingId} permissions={[PermissionKeys.Space.View]} fallback={<NoPackageAccessPage />}>
      <PageWithSidebarLayout
        sidebar={
          <div className=" flex flex-col overflow-hidden">
            <PageNavigation>
              <PageBreadcrumbs className="py-4">
                <PageBreadcrumbs.Link onClick={handleSelectBuildingsLinks}>
                  {ready ? t('overview') : <Skeleton variant="text" width="w-24" />}
                </PageBreadcrumbs.Link>
                <PageBreadcrumbs.Link>
                  {ready ? t('building.title') : <Skeleton variant="text" width="w-24" />}
                </PageBreadcrumbs.Link>
              </PageBreadcrumbs>
            </PageNavigation>

            <PageContentAnimation>
              <div className="flex items-center mt-2 mb-2">
                <div>
                  <BackButton />
                </div>
                <div className="flex-grow" />
                {/* <div className="flex space-x-2 items-center ml-4">
                  <Button
                    fontSize="sm"
                    color={displayOpen ? 'primary' : 'primary-dark'}
                    disabledVariant="dark"
                    onClick={() => setDisplayOpen(() => !displayOpen)}
                  >
                    {tCommon('display')}
                    {hasTags && (
                      <div className={classNames('-mr-2 ml-2 text-xs rounded-xl px-2.5 bg-brand-primary-active')}>
                        {tagCount}
                      </div>
                    )}
                  </Button>
                </div> */}
              </div>

              <SpaceTreeDisplay open={displayOpen} />

              <div className="mb-4 mt-2 overflow-y-auto scroll-smooth flex-1">
                <Tree
                  rootId={'root'}
                  tree={!isLoading ? treeData : skeletonTreeData}
                  render={(node, { depth, isOpen, onToggle, hasChild, isDropTarget, draggable }) => (
                    <SpaceNode
                      node={node}
                      depth={depth}
                      isOpen={isOpen}
                      hasChild={hasChild}
                      onToggle={onToggle}
                      isDropTarget={isDropTarget}
                      draggable={draggable}
                      isFetching={isFetching}
                    />
                  )}
                  dragPreviewRender={(monitorProps) => <SpaceDragPreview MonitorProps={monitorProps} />}
                  placeholderRender={(node, { depth }) => <SpaceTreePlaceholder node={node} depth={depth} />}
                  onChangeOpen={setOpenIds}
                  onDrop={handleDrop}
                  canDrop={(tree, { dragSource, dropTarget, dragSourceId, dropTargetId }) => {
                    const dropTargetDiscriminator = dropTarget?.data?.item?.discriminator;
                    const dragSourceDiscriminator = dragSource?.data?.item.discriminator;

                    // in case the dragged item will be dropped into himself
                    if (dropTargetId === dragSourceId) {
                      return false;
                    }

                    // in case the dragged item will be dropped in one of his descendants
                    if (dragSourceId && isNodeAncestorOf(tree, dragSourceId, dropTargetId)) {
                      return false;
                    }

                    if (dropTargetDiscriminator == null) {
                      return false;
                    }

                    let allowedTargets: SpaceDiscriminator[] = [];
                    switch (dragSourceDiscriminator) {
                      case 'Generic':
                        allowedTargets = ['Building', 'Generic', 'ResidentialUnit', 'CommercialSpace'];
                        break;
                      case 'CommercialSpace':
                        allowedTargets = ['Building', 'Generic'];
                        break;
                      case 'ResidentialUnit':
                        allowedTargets = ['Building', 'Generic'];
                        break;
                      case 'CommonArea':
                        allowedTargets = ['Building', 'Generic', 'ResidentialUnit', 'CommercialSpace'];
                        break;
                      case 'Room':
                        allowedTargets = ['ResidentialUnit'];
                        break;
                      case 'ParkingSpot':
                        allowedTargets = ['Building', 'Generic', 'ResidentialUnit', 'CommercialSpace'];
                        break;
                      default:
                        allowedTargets = [];
                        break;
                    }

                    return allowedTargets.includes(dropTargetDiscriminator);
                  }}
                  canDrag={(node) => !isLoading && !isFetching && node?.id !== buildingId}
                  initialOpen={isLoading || [...openIds]}
                  sort={false}
                  insertDroppableFirst={false}
                  enableAnimateExpand
                  dropTargetOffset={8}
                  classes={{
                    root: 'h-full',
                    draggingSource: 'opacity-30',
                    placeholder: 'relative',
                  }}
                />
              </div>
            </PageContentAnimation>
          </div>
        }
      >
        <div className="overflow-y-auto scroll-smooth">
          <Outlet />
        </div>
      </PageWithSidebarLayout>
    </HasSpacePermission>
  );
}
